import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  MapPin, Users, TrendingUp, DollarSign, Award, Globe, ChevronDown, ChevronUp,
  ArrowRight, Star, Zap, Shield, Heart, Gift
} from 'lucide-react';

const AffiliationProgramPage = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = (fr, en) => language === 'fr' ? fr : en;
  const [openFaq, setOpenFaq] = useState(null);

  const ranks = [
    { key: 'mercure', fr: 'Mercure', en: 'Mercury', emoji: '🪨', orbits: 12, color: '#9CA3AF', enrolled: 0, volume: 0, potential: '$2,047' },
    { key: 'venus', fr: 'Vénus', en: 'Venus', emoji: '🌕', orbits: 13, color: '#FCD34D', enrolled: 3, volume: 500, potential: '$4,095' },
    { key: 'terre', fr: 'Terre', en: 'Earth', emoji: '🌍', orbits: 13, color: '#34D399', enrolled: 5, volume: 2000, potential: '$4,095' },
    { key: 'mars', fr: 'Mars', en: 'Mars', emoji: '🔴', orbits: 14, color: '#F87171', enrolled: 10, volume: 5000, potential: '$8,191' },
    { key: 'jupiter', fr: 'Jupiter', en: 'Jupiter', emoji: '🟠', orbits: 14, color: '#FB923C', enrolled: 20, volume: 15000, potential: '$8,191' },
    { key: 'saturne', fr: 'Saturne', en: 'Saturn', emoji: '🪐', orbits: 15, color: '#C084FC', enrolled: 50, volume: 50000, potential: '$16,383' },
  ];

  const commissionTypes = [
    {
      icon: TrendingUp, color: 'purple',
      title_fr: 'Commissions Matrice', title_en: 'Matrix Commissions',
      desc_fr: 'Recevez 2.5% par orbite sur les paiements mensuels de votre réseau. Plus votre rang monte, plus d\'orbites sont débloquées (jusqu\'à 15).',
      desc_en: 'Receive 2.5% per orbit on your network\'s monthly payments. The higher your rank, the more orbits are unlocked (up to 15).'
    },
    {
      icon: Zap, color: 'amber',
      title_fr: 'Bonus Démarrage Rapide', title_en: 'Fast Start Bonus',
      desc_fr: 'Gagnez un bonus immédiat pour chaque nouveau membre ou affilié que vous inscrivez. $5 par membre + $20 par affilié.',
      desc_en: 'Earn an immediate bonus for each new member or affiliate you enroll. $5 per member + $20 per affiliate.'
    },
    {
      icon: Gift, color: 'emerald',
      title_fr: 'Bonus de Correspondance', title_en: 'Matching Bonus',
      desc_fr: 'Recevez un pourcentage des gains de matrice de vos recrues directes et de leurs recrues sur 5 générations (50%, 10%, 5%, 5%, 3%).',
      desc_en: 'Receive a percentage of the matrix earnings of your direct recruits and their recruits over 5 generations (50%, 10%, 5%, 5%, 3%).'
    }
  ];

  const steps = [
    { num: '01', title_fr: 'Inscrivez-vous', title_en: 'Sign Up', desc_fr: 'Créez votre compte gratuit et rejoignez la communauté LocalSite.Club.', desc_en: 'Create your free account and join the LocalSite.Club community.' },
    { num: '02', title_fr: 'Devenez affilié', title_en: 'Become an Affiliate', desc_fr: 'Activez votre statut d\'affilié pour débloquer le programme de commissions (49.95$).', desc_en: 'Activate your affiliate status to unlock the commission program ($49.95).' },
    { num: '03', title_fr: 'Partagez votre code', title_en: 'Share Your Code', desc_fr: 'Utilisez votre code de parrainage unique pour inviter des membres et des entreprises locales.', desc_en: 'Use your unique referral code to invite members and local businesses.' },
    { num: '04', title_fr: 'Gagnez des commissions', title_en: 'Earn Commissions', desc_fr: 'Recevez des commissions automatiques chaque mois sur tout votre réseau.', desc_en: 'Receive automatic commissions every month from your entire network.' }
  ];

  const faqItems = [
    {
      q_fr: 'Combien ça coûte pour devenir affilié ?',
      q_en: 'How much does it cost to become an affiliate?',
      a_fr: 'L\'abonnement membre est de 9.95$/mois. Les frais d\'affiliation sont de 49.95$ (paiement unique). En tant qu\'affilié, vous avez accès au programme complet de commissions.',
      a_en: 'The member subscription is $9.95/month. The affiliation fee is $49.95 (one-time payment). As an affiliate, you have access to the full commission program.'
    },
    {
      q_fr: 'Comment fonctionne la matrice 4x7 ?',
      q_en: 'How does the 4x7 matrix work?',
      a_fr: 'Chaque membre peut inscrire jusqu\'à 4 personnes directes (Nord, Sud, Est, Ouest). La matrice s\'étend sur 7 niveaux de profondeur, ce qui représente un potentiel de 21 844 membres dans votre réseau.',
      a_en: 'Each member can enroll up to 4 direct people (North, South, East, West). The matrix extends over 7 levels of depth, representing a potential of 21,844 members in your network.'
    },
    {
      q_fr: 'Quand et comment je reçois mes commissions ?',
      q_en: 'When and how do I receive my commissions?',
      a_fr: 'Les commissions de matrice sont calculées mensuellement. Les bonus de démarrage rapide sont crédités immédiatement. Les retraits sont possibles dès 10$ de solde disponible.',
      a_en: 'Matrix commissions are calculated monthly. Fast start bonuses are credited immediately. Withdrawals are possible from $10 available balance.'
    },
    {
      q_fr: 'Quelle est la philosophie "Zero Middleman" ?',
      q_en: 'What is the "Zero Middleman" philosophy?',
      a_fr: 'LocalSite.Club ne prend aucune commission sur les transactions entre membres et entreprises. Nous sommes un répertoire et une communauté, pas un intermédiaire commercial.',
      a_en: 'LocalSite.Club takes no commission on transactions between members and businesses. We are a directory and community, not a commercial intermediary.'
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--ls-sand)]" data-testid="affiliation-program-page">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--ls-bark) 0%, #2D1F15 60%, #1E1510 100%)' }}>
        {/* Language Toggle */}
        <div className="absolute top-4 right-4 z-20 flex bg-white/10 rounded-full overflow-hidden text-xs">
          <button onClick={() => language !== 'fr' && toggleLanguage()}
            className={`px-3 py-1.5 font-semibold transition-colors ${language === 'fr' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-white/50'}`}>FR</button>
          <button onClick={() => language !== 'en' && toggleLanguage()}
            className={`px-3 py-1.5 font-semibold transition-colors ${language === 'en' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-white/50'}`}>EN</button>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-8 text-white/60 hover:text-white transition-colors">
            <MapPin className="w-5 h-5 text-[var(--ls-terracotta)]" />
            <span className="font-heading font-bold">Local<span className="text-[var(--ls-terracotta)]">Site</span>.Club</span>
          </Link>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight leading-tight">
            {t('Programme d\'Affiliation', 'Affiliate Program')}
          </h1>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mb-8">
            {t(
              'Rejoignez notre communauté et gagnez des commissions en aidant les entreprises locales à se faire connaître. Matrice 4x7, trois types de commissions, zéro intermédiaire.',
              'Join our community and earn commissions by helping local businesses get noticed. 4x7 matrix, three commission types, zero middleman.'
            )}
          </p>

          <div className="flex flex-wrap gap-3">
            <Link to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--ls-terracotta)] text-white font-semibold hover:bg-[var(--ls-terracotta-dark)] transition-colors shadow-lg shadow-[var(--ls-terracotta)]/20"
              data-testid="affiliation-cta-register">
              {t('Devenir affilié', 'Become an Affiliate')} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/80 font-medium hover:bg-white/5 transition-colors">
              {t('Déjà membre ? Connexion', 'Already a member? Sign in')}
            </Link>
          </div>

          {/* Decorative orbits */}
          <div className="absolute top-10 right-10 w-64 h-64 border border-white/5 rounded-full opacity-30 hidden lg:block" />
          <div className="absolute top-20 right-20 w-40 h-40 border border-white/5 rounded-full opacity-20 hidden lg:block" />
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[var(--ls-text)] text-center mb-12">
          {t('Comment ça fonctionne', 'How It Works')}
        </h2>
        <div className="grid md:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div key={i} className="relative bg-white rounded-2xl p-6 border border-[hsl(40,20%,88%)] shadow-sm hover:shadow-md transition-shadow">
              <span className="text-4xl font-bold text-[var(--ls-terracotta)]/20">{step.num}</span>
              <h3 className="font-heading text-lg font-bold text-[var(--ls-text)] mt-2 mb-2">
                {language === 'fr' ? step.title_fr : step.title_en}
              </h3>
              <p className="text-sm text-[var(--ls-text-muted)]">
                {language === 'fr' ? step.desc_fr : step.desc_en}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Commission Types */}
      <section className="bg-[var(--ls-bark)] py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white text-center mb-4">
            {t('3 Sources de Revenus', '3 Revenue Streams')}
          </h2>
          <p className="text-white/50 text-center mb-12 max-w-lg mx-auto">
            {t('Gagnez des commissions de trois façons différentes avec notre système MLM.', 'Earn commissions in three different ways with our MLM system.')}
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {commissionTypes.map((ct, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${ct.color}-500/10 mb-4`}>
                  <ct.icon className={`w-6 h-6 text-${ct.color}-400`} />
                </div>
                <h3 className="font-bold text-white text-lg mb-2">
                  {language === 'fr' ? ct.title_fr : ct.title_en}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {language === 'fr' ? ct.desc_fr : ct.desc_en}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ranks Table */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[var(--ls-text)] text-center mb-4">
          {t('Progression des Rangs', 'Rank Progression')}
        </h2>
        <p className="text-[var(--ls-text-muted)] text-center mb-10 max-w-lg mx-auto">
          {t('Montez en rang pour débloquer plus d\'orbites et maximiser vos revenus.', 'Move up in rank to unlock more orbits and maximize your earnings.')}
        </p>
        <div className="bg-white rounded-2xl border border-[hsl(40,20%,88%)] overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--ls-sand)] border-b border-[hsl(40,20%,88%)]">
                  <th className="text-left px-4 py-3 font-semibold text-[var(--ls-text)]">{t('Rang', 'Rank')}</th>
                  <th className="text-center px-4 py-3 font-semibold text-[var(--ls-text)]">{t('Orbites', 'Orbits')}</th>
                  <th className="text-center px-4 py-3 font-semibold text-[var(--ls-text)] hidden md:table-cell">{t('Inscrits requis', 'Required Enrollees')}</th>
                  <th className="text-center px-4 py-3 font-semibold text-[var(--ls-text)] hidden md:table-cell">{t('Volume requis', 'Required Volume')}</th>
                  <th className="text-right px-4 py-3 font-semibold text-[var(--ls-text)]">{t('Potentiel / mois', 'Potential / month')}</th>
                </tr>
              </thead>
              <tbody>
                {ranks.map((r, i) => (
                  <tr key={r.key} className={`border-b border-[hsl(40,20%,88%)]/50 ${i % 2 === 0 ? '' : 'bg-[var(--ls-sand)]/30'}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{r.emoji}</span>
                        <span className="font-semibold text-[var(--ls-text)]" style={{ color: r.color }}>{language === 'fr' ? r.fr : r.en}</span>
                      </div>
                    </td>
                    <td className="text-center px-4 py-3 text-[var(--ls-text)]">{r.orbits}</td>
                    <td className="text-center px-4 py-3 text-[var(--ls-text-muted)] hidden md:table-cell">{r.enrolled}</td>
                    <td className="text-center px-4 py-3 text-[var(--ls-text-muted)] hidden md:table-cell">${r.volume.toLocaleString()}</td>
                    <td className="text-right px-4 py-3 font-bold" style={{ color: r.color }}>{r.potential}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[var(--ls-text)] text-center mb-10">
            {t('Questions fréquentes', 'Frequently Asked Questions')}
          </h2>
          <div className="space-y-3">
            {faqItems.map((faq, i) => (
              <div key={i} className="border border-[hsl(40,20%,88%)] rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[var(--ls-sand)]/50 transition-colors"
                  data-testid={`faq-toggle-${i}`}>
                  <span className="font-medium text-[var(--ls-text)] pr-4">
                    {language === 'fr' ? faq.q_fr : faq.q_en}
                  </span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-[var(--ls-text-muted)] shrink-0" /> : <ChevronDown className="w-5 h-5 text-[var(--ls-text-muted)] shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-[var(--ls-text-muted)] leading-relaxed">
                    {language === 'fr' ? faq.a_fr : faq.a_en}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, var(--ls-bark) 0%, #2D1F15 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            {t('Prêt à commencer ?', 'Ready to Get Started?')}
          </h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">
            {t(
              'Rejoignez des milliers de membres qui soutiennent leurs commerces locaux tout en gagnant des commissions.',
              'Join thousands of members supporting their local businesses while earning commissions.'
            )}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/register"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[var(--ls-terracotta)] text-white font-semibold hover:bg-[var(--ls-terracotta-dark)] transition-colors shadow-lg shadow-[var(--ls-terracotta)]/20"
              data-testid="affiliation-cta-bottom">
              {t('S\'inscrire maintenant', 'Sign Up Now')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--ls-bark)] py-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-white/40 text-sm">
            <MapPin className="w-4 h-4" />
            <span>LocalSite.Club — {t("Let's make friends", "Let's make friends")}</span>
          </div>
          <div className="flex gap-4 text-sm">
            <Link to="/" className="text-white/40 hover:text-white/70 transition-colors">{t('Accueil', 'Home')}</Link>
            <Link to="/browse" className="text-white/40 hover:text-white/70 transition-colors">{t('Répertoire', 'Directory')}</Link>
            <Link to="/login" className="text-white/40 hover:text-white/70 transition-colors">{t('Connexion', 'Login')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AffiliationProgramPage;
